/**
 * F-18 — a fixture's warnings, collapsed to one line each.
 *
 * The screen used to print every warning as a full sentence under its fixture. On a bad day
 * that is four paragraphs between one match and the next, and the Technical Delegate stops
 * reading them — which is the exact failure mode a warning exists to avoid. So each warning
 * is now a tag chip and a short cause: `RULE BREACH · MBEKI-LARSSON — own country`. The full
 * sentence, and the actions that go with it, are one click away.
 *
 * Two rules kept from the previous surface, because they are not cosmetic:
 *
 *  * A hard breach is announced as an `alert`; everything softer is a polite `status`
 *    (`warningRole`). Collapsing the text does not change how urgently it is read out.
 *  * `fixture_already_official` is an **anomaly note**, never an error (§F). TMS is the
 *    record and nothing is blocked, so it keeps its own amber chip and its own words.
 */

import { useState } from "react";

import type { AppointmentWarning } from "@hockeytd/shared";

import type { OfficialView } from "../../api/types.js";
import { warningClass, warningKind, warningRole } from "./vocabulary.js";

export interface FixtureWarningsProps {
  warnings: readonly AppointmentWarning[];
  officials: readonly OfficialView[];
}

/**
 * The one line a collapsed warning shows — who, and what, in as few words as carry the point.
 *
 * Composed from the warning's own machine-readable parts rather than by truncating its
 * sentence: a cut sentence loses the half that matters as often as not, and this way the
 * short cause and the full sentence can be checked against each other.
 */
export function shortCause(
  warning: AppointmentWarning,
  officials: readonly OfficialView[],
): string {
  const official = officials.find((entry) => entry.tmsPersonId === warning.official_id);
  const who = official === undefined ? null : official.surnames.toUpperCase();
  const slot = warning.slot === null ? "" : ` ${warning.slot}`;

  switch (warning.code) {
    case "country_conflict":
      return `${who ?? "This official"}${slot} · own country`;
    case "two_roles_one_match":
      return `${who ?? "This official"} · two roles in one match`;
    case "unfilled_slot":
      // The engine says WHY the slot is empty: a stored slot no draft has touched is merely
      // "not yet appointed", and only a draft pass that exhausted its candidates earns
      // "nobody could be drafted". Older payloads carry no cause; they can only have come
      // from a draft, so the drafting words are the right fallback.
      return warning.detail["cause"] === "not_yet_appointed"
        ? `${warning.slot ?? "A slot"} · not yet appointed`
        : `${warning.slot ?? "A slot"} · nobody could be drafted`;
    case "sj_tj_imbalance":
      return `${who ?? "A judge"} · SJ / TJ spread ${String(warning.detail["difference"] ?? "")}`.trim();
    case "daily_cap_exceeded":
      return `${who ?? "This official"} · ${String(warning.detail["count"] ?? "")} appointments today`;
    case "team_exposure_heat":
      return `${who ?? "This official"} · ${String(warning.detail["team"] ?? "one team")} ${String(
        warning.detail["count"] ?? "",
      )} times`;
    case "fixture_already_official":
      return "Already Official in TMS";
    // RUN 38 — the one warning whose short cause names TWO officials, so it takes the pair
    // straight from `detail` rather than looking one up from `official_id`, which holds only
    // the first of them.
    case "co_appointment_concentration":
      return (
        `${String(warning.detail["pair"] ?? "Two officials")} · share ` +
        `${String(warning.detail["shared"] ?? "")} matches across the tournament — tolerance ` +
        `${String(warning.detail["tolerance"] ?? "")}`
      );
    default:
      return warning.message;
  }
}

export function FixtureWarnings({
  warnings,
  officials,
}: FixtureWarningsProps): React.JSX.Element {
  return (
    <div className="warnlist">
      {warnings.map((warning, index) => (
        <WarningLine
          key={`${warning.code}-${String(index)}`}
          warning={warning}
          officials={officials}
        />
      ))}
    </div>
  );
}

export function WarningLine({
  warning,
  officials,
}: {
  warning: AppointmentWarning;
  officials: readonly OfficialView[];
}): React.JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    // The role sits on the container, not on the expanded sentence: collapsing a warning must
    // not quieten it. What is announced is the chip and the short cause, which is the whole of
    // what the collapsed line says.
    <div
      className={warningClass(warning)}
      role={warningRole(warning)}
      data-testid={`warning-${warning.code}`}
    >
      <button
        type="button"
        className="warnnote__toggle"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <span className="warnnote__kind">{warningKind(warning)}</span>
        <span className="warnnote__cause">{shortCause(warning, officials)}</span>
        <span className="warnnote__chev" aria-hidden="true">
          {open ? "▴" : "▾"}
        </span>
      </button>
      {open ? (
        <p className="warnnote__msg" data-testid={`warning-detail-${warning.code}`}>
          {warning.message}
        </p>
      ) : null}
    </div>
  );
}
